import * as Sentry from "@sentry/react-native";
import { useQueryClient } from "@tanstack/react-query";

import { useApiUrl } from "@luna-restaurant/school-api";

import { authStorage } from "@/services/authStorage";
import { resetSessionExpired } from "@/services/sessionExpiryEvents";

type LoginPayload = {
  email: string;
  password: string;
};

type LoginResponse = {
  token?: string;
  refresh_token?: string;
};

export const useAuthApi = () => {
  const apiUrl = useApiUrl();
  const queryClient = useQueryClient();

  const login = async ({
    email,
    password,
  }: LoginPayload): Promise<{ accessToken: string }> => {
    const url = apiUrl("/v1/login");
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ username: email, password }),
    });

    if (!res.ok) {
      let message = "Login failed";
      try {
        const body = await res.json();
        message = body?.title ?? body?.message ?? message;
      } catch (e) {
        Sentry.captureException(e);
        console.error("Failed to parse login error response", e);
      }
      throw new Error(message);
    }

    const data = (await res.json()) as LoginResponse;
    const accessToken = data.token;
    const refreshToken = data.refresh_token ?? null;

    if (!accessToken || !refreshToken) {
      throw new Error("Invalid login response: tokens missing");
    }

    await authStorage.setAccessToken(accessToken);
    await authStorage.setRefreshToken(refreshToken);
    queryClient.setQueryData(["accessToken"], accessToken);
    resetSessionExpired();

    return { accessToken };
  };

  const refreshAccessToken = async () => {
    const refreshToken = await authStorage.getRefreshToken();
    if (!refreshToken) return null;

    const url = apiUrl("/v1/refresh");
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) {
      if (res.status === 401) {
        await authStorage.setRefreshToken(null);
      }
      return null;
    }

    const data = (await res.json()) as {
      token?: string;
      refresh_token?: string;
      refresh_token_expiration?: number;
    };
    if (!data.token) {
      return null;
    }

    await authStorage.setAccessToken(data.token);
    queryClient.setQueryData(["accessToken"], data.token);
    if (data.refresh_token) {
      await authStorage.setRefreshToken(data.refresh_token);
    }
    return data.token;
  };

  const logout = async () => {
    try {
      const refreshToken = await authStorage.getRefreshToken();
      if (refreshToken) {
        const url = apiUrl("/v1/logout");
        await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "x-refresh-token": refreshToken,
          },
        });
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error("Failed to invalidate refresh token on logout", e);
    }

    await authStorage.clear();
    queryClient.removeQueries({ queryKey: ["accessToken"] });
    queryClient.removeQueries({ queryKey: ["currentUser"] });
    queryClient.removeQueries({ queryKey: ["currentSchool"] });
  };

  return { login, logout, refreshAccessToken };
};
